import { motion } from 'framer-motion';
import { StudentClusteringProps } from '../../../types';

const StudentClustering: React.FC<StudentClusteringProps> = ({ clusters }) => {
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Student Learning Groups</h3>
        <p className="text-gray-600 max-w-3xl mx-auto">
          Using K-means clustering analysis of student characteristics, we've identified distinct learning groups 
          in your class. Each group has unique traits and responds to different teaching strategies.
        </p>
      </div>

      {/* Clustering Visualization */}
      <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
        <h4 className="text-xl font-semibold text-gray-900 mb-6 text-center">Class Composition</h4>
        <div className="flex justify-center items-center space-x-8">
          {clusters.map((cluster) => (
            <motion.div
              key={cluster.id}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center text-white font-bold text-lg mb-3 mx-auto"
                style={{ backgroundColor: cluster.color }}
              >
                {cluster.size}
              </div>
              <h5 className="font-medium text-gray-900 text-sm">{cluster.name}</h5>
              <p className="text-gray-500 text-xs">{cluster.size} students</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Detailed Group Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {clusters.map((cluster, index) => (
          <motion.div
            key={cluster.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center mb-4">
              <div
                className="w-4 h-4 rounded-full mr-3"
                style={{ backgroundColor: cluster.color }}
              />
              <h4 className="text-lg font-semibold text-gray-900">{cluster.name}</h4>
              <span className="ml-auto text-sm text-gray-500">{cluster.size} students</span>
            </div>
            
            <div className="space-y-4">
              <div>
                <h5 className="font-medium text-gray-900 mb-2">Characteristics</h5>
                <ul className="space-y-1">
                  {cluster.characteristics.map((char, i) => (
                    <li key={i} className="text-sm text-gray-700 flex items-start">
                      <span className="text-emerald-500 mr-2">•</span>
                      {char}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h5 className="font-medium text-gray-900 mb-2">Targeted Strategies</h5>
                <ul className="space-y-1">
                  {cluster.suggestions.map((suggestion, i) => (
                    <li key={i} className="text-sm text-gray-700 flex items-start">
                      <span className="text-blue-500 mr-2">→</span>
                      {suggestion}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default StudentClustering; 